/* eslint-disable max-lines, no-unused-expressions, no-magic-numbers, curly, no-useless-return */
import scrollTo from 'ui/scrollTo';
import ResizeTracker from 'utils/ResizeTracker';
import { debounce } from 'utils/debounce';

class Carousel {

    constructor(element, options) {
        this._element = element;

        this._options = {
            window: null,
            slides: null,
            threshold: 0,
            breakpoint: 768,
            timeout: 300,
            pace: 1000,
            debounceTime: 50,
            defaultTag: null,
            shortDebounceTime: 10,
            ...options
        };

        this._state = {
            index: 0,
            direction: 'left',
            isMobile: false,
            currentScroll: 0,
            timeout: null,
            isInfinite: false,
            dynamicScroll: false,
            slideOffset: 0,
            slideWidth: 0,
            allSlides: null,
            filteredSlides: null,
            currentIndex: 0,
            observer: null,
            countDown: 0,
            startedTime: null,
            resumeTime: null,
            status: 'pause',
            isControlled: false,
            scrollTimeout: null,
            isPageScrolling: false,
            stopSlider: false,
            endTimeout: null,
            startTimeout: null
        };

        this._handleResize = this._handleResize.bind(this);
        this._handleClickedTag = this._handleClickedTag.bind(this);
        this._filterSlides = this._filterSlides.bind(this);
        this._showAllSlides = this._showAllSlides.bind(this);
        this._handleClickedBullet = this._handleClickedBullet.bind(this);
        this._handleClickedControlTag = this._handleClickedControlTag.bind(this);

        this._cacheSelectors();
        this._setResizeTracker();
        this._setLiveRegionOptions();
        this._addEventListeners();
        this._init();
    }

    /**
     * Cache selectors
     *
     * @memberof Carousel
     */
    _cacheSelectors() {
        this._elements = {
            window: this._element.querySelector(this._options.window),
            slides: [...this._element.querySelectorAll(this._options.slides)],
            prev: this._element.querySelector(this._options.previous),
            next: this._element.querySelector(this._options.next),
            body: document.querySelector('body'),
            tags: this._options.tags && [...this._element.querySelectorAll(this._options.tags)],
            controlTags: this._options.controlTags === 'true' && [...this._element.querySelectorAll('.control-tag')],
            copyContainer: this._element.querySelector('.slides__copy'),
            liveRegion: this._element.querySelector('[aria-live]'),
            tagCounter: this._element.querySelector('[aria-live] .counter'),
            tagLabel: this._element.querySelector('[aria-live] .tag'),
            tagSentence: this._element.querySelector('[aria-live] .text'),
            bullets: [...this._element.querySelectorAll('.carousel__bullet-link')]
        };
    }

    /**
     * Add listeners
     *
     * @memberof Carousel
     */
    _addEventListeners() {
        this._options.previous && this._elements.prev && this._elements.prev.addEventListener('click', debounce(() => {
            this._previous();
        }, this._options.timeout));

        this._options.next && this._elements.next && this._elements.next.addEventListener('click', debounce(() => {
            this._next();
        }, this._options.timeout));

        this._elements.tags && this._elements.tags.forEach(tag => {
            tag.addEventListener('click', event => this._handleClickedTag(event));
        });

        this._elements.controlTags && this._elements.controlTags.forEach(tag => {
            tag.addEventListener('click', event => this._handleClickedControlTag(event));
        });

        this._elements.bullets && this._elements.bullets.forEach(bullet => {
            bullet.addEventListener('click', event => this._handleClickedBullet(event));
        });

        this._elements.slides.forEach(slide => {
            this._addSlideListener(slide);
        });

    }

    /**
     * Init the carousel
     *
     * @memberof Carousel
     */
    _init() {
        this._element.setAttribute('data-initialized', 'true');

        this._state.allSlides = this._element.querySelectorAll(this._options.slides);
        this._state.filteredSlides = this._elements.slides;
        this._setupObserver();

        this._elements.window.setAttribute('data-scrolling', 'false');

        this._state.slideWidth = this._state.allSlides[1] ? this._state.allSlides[1].offsetWidth : this._state.allSlides[0].offsetWidth;
    }

    _scrollToFirstSlide() {
        scrollTo(null, -this._state.slideWidth, 'smooth', 'horizontal', this._elements.window);
    }

    _scrollToLastSlide() {
        scrollTo(null, this._state.slideWidth * this._state.allSlides.length, 'smooth', 'horizontal', this._elements.window);
    }

    /**
     * Add hover listener on each slide
     *
     * @memberof Carousel
     * @param {*} slide
     */
    _addSlideListener(slide) {
        slide.addEventListener('focus', () => {
            this._state.isPageScrolling = false;
            const slideIndex = parseInt(slide.getAttribute('data-index'), 10);

            if (slideIndex === 0) {
                this._scrollToFirstSlide();

                return;
            }

            if (this._state.allSlides.length - 1 === slideIndex) {
                this._scrollToLastSlide();
            }
        }, true);

        slide.addEventListener('mouseover', () => {
            this._state.isPageScrolling = true;
        });

        slide.addEventListener('mouseout', () => {
            this._state.isPageScrolling = true;
        });
    }

    /**
     * Set ResizeTracker
     *
     * @memberof Carousel
     */
    _setResizeTracker() {
        this._resizeTracker = new ResizeTracker();

        this._resizeTracker.init(
            { element: window },
            this._handleResize
        );
    }

    /**
     * Change values on resize
     *
     * @memberof Carousel
     */
    _handleResize() {
        this._removeObserver();
        this._setupObserver();
    }

    /**
     * Handle clicked tag
     *
     * @memberof Carousel
     * @param {event} event
     */
    _handleClickedTag(event) {
        event.preventDefault();
        const tag = event.target.closest(this._options.tags);
        const activeTag = tag.getAttribute('data-type');
        const showAll = activeTag === 'all';
        this._elements.tags.forEach(item => {
            item.closest(this._options.tags).setAttribute('aria-pressed', false);
        });
        tag.setAttribute('aria-pressed', true);
        const tagLabel = tag.querySelector('span').innerHTML;
        if (this._elements.tagLabel) {
            this._elements.tagLabel.innerHTML = tagLabel;
        }

        this._elements.window.setAttribute('data-active-tag', activeTag);

        !showAll && this._filterSlides(activeTag);
        showAll && this._showAllSlides();
    }

    /**
     * Set control tag active state
     *
     * @memberof Carousel
     */
    _setActiveControlTags() {
        if (!this._elements.controlTags) return;
        this._elements.controlTags.forEach(tag => {
            const tagIndex = parseInt(tag.getAttribute('data-index'), 10);
            tag.setAttribute('aria-pressed', tagIndex === this._state.currentIndex);
        });
    }

    /**
     * Filter slides depending on tags
     *
     * @memberof Carousel
     * @param {string} activeTag
     */
    _filterSlides(activeTag) {
        this._state.isPageScrolling = false;

        const timeout = setTimeout(() => {
            this._removeObserver();
            this._elements.window.innerHTML = '';
            let counter = 0;
            this._state.filteredSlides = [];
            this._state.isAutomatic && this._state.allSlides.forEach(slide => this._removeSlideListener(slide));
            const filteredSlides = [...this._state.allSlides].filter(slide => slide.getAttribute('data-type') === activeTag);
            this._elements.bullets.forEach(bullet => bullet.setAttribute('data-visible', false));
            filteredSlides.forEach(slide => {
                const clonedSlide = slide.cloneNode(true);
                // eslint-disable-next-line no-plusplus
                clonedSlide && counter++;
                this._elements.window.append(clonedSlide);
                this._state.filteredSlides.push(clonedSlide);
                const slideIndex = clonedSlide.getAttribute('data-index');
                this._elements.bullets && this._elements.bullets[slideIndex] && this._elements.bullets[slideIndex].setAttribute('data-visible', true);
                this._state.isAutomatic && this._addSlideListener(clonedSlide);
            });

            if (this._elements.liveRegion) {
                const tagSentence = counter > 1 ? this._options.liveRegionLabels.multiple : this._options.liveRegionLabels.single;
                this._elements.tagCounter.innerHTML = `${counter} ${tagSentence} ${activeTag}`;
            }

            this._handleResize();
            this._setupObserver();
            clearTimeout(timeout);
        }, this._options.timeout);
    }

    /**
     * Show all slides
     *
     * @memberof Carousel
     */
    _showAllSlides() {
        this._removeObserver();
        this._elements.window.innerHTML = '';
        this._state.filteredSlides = [];
        this._elements.bullets.forEach(bullet => bullet.setAttribute('data-visible', true));
        const allSlides = [...this._state.allSlides];
        allSlides.forEach(slide => {
            const clonedSlide = slide.cloneNode(true);
            this._elements.window.append(clonedSlide);
            this._state.filteredSlides.push(clonedSlide);
        });

        const timeout = setTimeout(() => {
            this._elements.window.setAttribute('data-active-tag', 'all');

            if (this._elements.liveRegion) {
                const tagSentence = this._state.allSlides.length > 1 ? this._options.liveRegionLabels.multiple : this._options.liveRegionLabels.single;
                this._elements.tagCounter.innerHTML = `${this._state.allSlides.length} ${tagSentence} ${this._options.defaultTag}`;
            }

            this._setupObserver();
            this._handleResize();
            clearTimeout(timeout);
        }, this._options.timeout);


    }

    /**
     * Handle active bullet
     *
     * @memberof Carousel
     */
    _handleControls() {
        this._elements.bullets.forEach(bullet => {
            const bulletIndex = parseInt(bullet.getAttribute('data-index'), 10);
            bulletIndex === this._state.currentIndex ? bullet.setAttribute('data-active', true) : bullet.removeAttribute('data-active');
        });
    }

    /**
     * Handle clicked bullet
     *
     * @memberof Carousel
     * @param {event} event
     */
    _handleClickedBullet(event) {
        event.preventDefault();
        this._state.resumeTime = 0;
        this._state.timer && clearTimeout(this._state.timer);

        this._elements.window.setAttribute('data-scrolling', true);
        const target = event.target;
        const index = parseInt(target.getAttribute('data-index'), 10);
        this._state.currentIndex = index;

        const currentSlide = this._elements.window.querySelector(`[data-index="${index}"]`);
        const offsetBox = currentSlide.getBoundingClientRect();
        const scrollLeft = this._elements.window.scrollX || this._elements.window.pageXOffset || this._elements.window.scrollLeft;

        const offset = offsetBox.left + scrollLeft;
        scrollTo(null, offset, 'smooth', 'horizontal', this._elements.window);

        const timeout = setTimeout(() => {
            this._elements.window.setAttribute('data-scrolling', false);
            clearTimeout(timeout);
        }, this._options.timeout);
    }

    /**
     * Go to next slide
     *
     * @memberof Carousel
     */
    _next() {
        this._showPrevButton();
        this._state.isPageScrolling = false;
        this._state.status = 'play';
        this._state.resumeTime = 0;
        this._state.startedTime = new Date();
        clearTimeout(this._state.timer);
        const offset = Math.ceil(this._elements.window.scrollLeft + this._state.slideWidth);
        this._scrollToSlide(offset);
    }

    /**
     * Go to previous slide
     *
     * @memberof Carousel
     */
    _previous() {
        this._showNextBtn();
        this._state.isPageScrolling = false;
        this._state.status = 'play';
        this._state.resumeTime = 0;
        this._state.startedTime = new Date();
        const offset = Math.ceil(this._elements.window.scrollLeft - this._state.slideWidth);
        this._scrollToSlide(offset);
    }

    /**
     * Scroll to specific offset
     *
     * @memberof Carousel
     * @param {number} offset
     */
    _scrollToSlide(offset) {
        clearTimeout(this._state.scrollTimeout);
        this._elements.window.setAttribute('data-scrolling', true);
        const scrollOffset = offset ? offset : 0;
        scrollTo(null, scrollOffset, 'smooth', 'horizontal', this._elements.window);

        this._state.scrollTimeout = setTimeout(() => {
            this._elements.window.setAttribute('data-scrolling', false);

            clearTimeout(this._state.scrollTimeout);
        }, this._options.timeout);
    }

    _hidePrevBtn() {
        this._element.setAttribute('data-start', 'true');
        this._elements.prev.setAttribute('data-active', 'false');
    }

    _showPrevButton() {
        this._element.setAttribute('data-start', 'false');
        this._elements.prev.setAttribute('data-active', 'true');
    }

    _hideNextBtn() {
        this._element.setAttribute('data-end', 'true');
        this._elements.next.setAttribute('data-active', 'false');
    }

    _showNextBtn() {
        this._elements.next.setAttribute('data-active', 'true');
        this._element.setAttribute('data-end', 'false');
    }

    /**
     * Set intersection observer on slides
     *
     * @memberof Carousel
     */
    _setupObserver() {
        const options = {
            root: this._elements.window,
            rootMargin: '10px',
            threshold: 0.4
        };

        this._state.observer = new IntersectionObserver(entries => {
            entries.forEach(entry => {
                const indexesInView = entries
                    .filter(item => item.isIntersecting)
                    .map(item => item.target);

                const isIntersecting = entry.isIntersecting && entry.intersectionRatio > 0.03;
                const target = entry.target;
                const entryIndex = parseInt(target.getAttribute('data-index'), 10);
                this._state.currentIndex = isIntersecting ? entryIndex : this._state.currentIndex;

                this._handleControls();
                this._setActiveControlTags();


                if (!isIntersecting) return;

                const firstItemInView = indexesInView.filter(item => item.getAttribute('data-index') === this._state.filteredSlides[0].getAttribute('data-index')).length > 0;

                const lastIndexInView = indexesInView[indexesInView.length - 1].getAttribute('data-index');
                const lastFilteredSlide = this._state.filteredSlides[this._state.filteredSlides.length - 1].getAttribute('data-index');

                const lastItemInview = lastIndexInView === lastFilteredSlide;

                if (firstItemInView && lastItemInview) {
                    this._hidePrevBtn();
                    this._hideNextBtn();
                }

                if (firstItemInView) {
                    this._scrollToFirstSlide();
                    this._hidePrevBtn();
                } else {
                    this._showPrevButton();
                }

                if (lastItemInview) {
                    this._scrollToLastSlide();
                    this._hideNextBtn();
                } else {
                    this._showNextBtn();
                }

            });

        }, options);

        this._state.observer.POLL_INTERVAL = 100;

        this._state.filteredSlides.forEach(slide => {
            this._state.observer.observe(slide);
        });

    }

    /**
     * Remove intersection observer
     *
     * @memberof Carousel
     */
    _removeObserver() {
        this._state.filteredSlides.forEach(slide => {
            this._state.observer && this._state.observer.unobserve(slide);
        });

    }

    /**
     * Get the live region labels
     *
     * @memberof Carousel
     */
    _setLiveRegionOptions() {
        if (!this._elements.liveRegion) return;

        const defaultTag = this._elements.tags && this._elements.tags[0] && this._elements.tags[0].querySelector('carousel__tag-title');
        this._options.defaultTag = defaultTag && defaultTag.textContent;

        const liveRegionLabels = {
            multiple: this._elements.liveRegion.getAttribute('data-multiple'),
            single: this._elements.liveRegion.getAttribute('data-single')
        };

        this._options.liveRegionLabels = liveRegionLabels;
    }

    /**
    * Handle control tag
    *
    * @memberof Carousel
    * @param {event} event
    */
    _handleClickedControlTag(event) {
        event.preventDefault();
        this._state.resumeTime = 0;
        this._state.timer && clearTimeout(this._state.timer);

        this._elements.window.setAttribute('data-scrolling', true);
        const target = event.target;
        const index = parseInt(target.getAttribute('data-index'), 10);
        this._state.currentIndex = index;
        const offset = index * this._state.slideWidth;
        scrollTo(null, offset, 'smooth', 'horizontal', this._elements.window);

        const timeout = setTimeout(() => {
            this._elements.window.setAttribute('data-scrolling', false);
            clearTimeout(timeout);
        }, this._options.timeout);
    }


}

export default Carousel;
